import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { trackAddToCart } from '@/lib/analytics';

/** Quantidade no carrinho = nº de pacotes (nunca unidades do serviço). */
export const MAX_PACKAGES_PER_LINE = 99;

export interface CartItem {
  id: string;
  productId: string;
  slug: string;
  name: string;
  priceCents: string;
  quantity: number;
  targetUrl?: string;
}

interface CartState {
  items: CartItem[];
  addItem: (item: Omit<CartItem, 'id'>) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clear: () => void;
  totalCents: () => number;
  packageCount: () => number;
}

function clampPackages(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return 1;
  return Math.min(MAX_PACKAGES_PER_LINE, Math.max(1, Math.floor(n)));
}

function sanitizeItems(items: CartItem[]): CartItem[] {
  return items.map((item) => ({
    ...item,
    quantity: clampPackages(item.quantity),
  }));
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      addItem: (item) => {
        trackAddToCart(item.productId, { slug: item.slug, quantity: item.quantity });
        set((state) => {
          const addQty = clampPackages(item.quantity);
          const existing = state.items.find((i) => i.productId === item.productId);
          if (existing) {
            return {
              items: state.items.map((i) =>
                i.productId === item.productId
                  ? { ...i, quantity: clampPackages(i.quantity + addQty) }
                  : i,
              ),
            };
          }
          return {
            items: [...state.items, { ...item, id: crypto.randomUUID(), quantity: addQty }],
          };
        });
      },
      removeItem: (id) => set((state) => ({ items: state.items.filter((i) => i.id !== id) })),
      updateQuantity: (id, quantity) =>
        set((state) => ({
          items: state.items.map((i) =>
            i.id === id ? { ...i, quantity: clampPackages(quantity) } : i,
          ),
        })),
      clear: () => set({ items: [] }),
      totalCents: () => get().items.reduce((sum, i) => sum + Number(i.priceCents) * i.quantity, 0),
      packageCount: () => get().items.reduce((n, i) => n + i.quantity, 0),
    }),
    {
      name: 'dz-cart-v2',
      version: 2,
      migrate: (persisted) => {
        const state = persisted as { items?: CartItem[] } | undefined;
        return { items: sanitizeItems(state?.items ?? []) };
      },
      merge: (persisted, current) => {
        const p = persisted as Partial<CartState> | undefined;
        return {
          ...current,
          ...p,
          items: sanitizeItems(p?.items ?? current.items),
        };
      },
    },
  ),
);

export function formatBRL(cents: number): string {
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}
